import { useState } from 'react';
import { Modal, ModalBody, ModalHeader, Text, List, ListItem } from '@mezzoforte/forge';

export type EntryModifiedModalType = 'VAT' | 'Prices';

interface ModalConfig {
  title: string;
  info: string;
  disabledFields: string[];
  guide: string;
}

const modalConfig: Record<EntryModifiedModalType, ModalConfig> = {
  VAT: {
    title: 'ALV-kantaan vaikuttavat kentät',
    info: 'Kohteen arvonlisäverokanta ei saa muuttua ilmoituksen julkaisun jälkeen. Seuraavat kentät eivät välttämättä ole enää muokattavia, koska ne vaikuttavat verokantaan:',
    disabledFields: [
      'Kohde on uusi/käytetty',
      'ALV %',
      'Kohteen myynti toimeksiantona',
      'Toimeksiantaja on yksityinen/yritys',
    ],
    guide:
      'Jos haluat vaihtaa valintaa, sinun on peruttava tämä kohde mahdollisine huutoineen ja tehtävä uusi ilmoitus.',
  },
  Prices: {
    title: 'Hintoihin vaikuttavat kentät ja päättymisaika',
    info: 'Seuraavia kenttiä voi muokata enintään 24 tunnin ajan ilmoituksen julkaisusta, kuitenkin siten, että muokkaus ei ole mahdollista viimeisen vuorokauden aikana tai jos kohteeseen on tehty yksikin tarjous:',
    disabledFields: ['Lähtöhinta', 'Minimikorotus', 'Toimitustapa ja -kulut', 'Päättymisaika', 'Osasto'],
    guide:
      'Lähtöhintaa ja minimikorotusta voi lisäksi muokata 24 tunnin jälkeen vain pienemmäksi. Lisäksi myyntitavan vaihtaminen voi muuttua rajoitetuksi.',
  },
};

interface EntryModifiedModalProps {
  isOpen: boolean;
  modalType: EntryModifiedModalType;
  onClose: () => void;
}

function EntryModifiedModal({ isOpen, modalType, onClose }: EntryModifiedModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onDismiss={() => onClose()}
      size={{ base: 'xs', sm: 'sm', lg: 'lg', xl: '4xl' }}
    >
      <ModalHeader>{modalConfig[modalType].title}</ModalHeader>
      <ModalBody>
        <Text>{modalConfig[modalType].info}</Text>
        <List my={1}>
          {modalConfig[modalType].disabledFields.map((field) => (
            <ListItem
              key={field}
              variant="default"
              fontSize="inherit"
            >
              {field}
            </ListItem>
          ))}
        </List>
        <Text>{modalConfig[modalType].guide}</Text>
      </ModalBody>
    </Modal>
  );
}

export const useEntryModifiedModal = () => {
  const [isVatModalOpen, setIsVatModalOpen] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);

  function PriceChangedModal() {
    return isPriceModalOpen ? (
      <EntryModifiedModal
        isOpen={isPriceModalOpen}
        modalType="Prices"
        onClose={() => setIsPriceModalOpen(false)}
      />
    ) : null;
  }

  function VatChangedModal() {
    return isVatModalOpen ? (
      <EntryModifiedModal
        isOpen={isVatModalOpen}
        modalType="VAT"
        onClose={() => setIsVatModalOpen(false)}
      />
    ) : null;
  }

  const openModal = (modalType: EntryModifiedModalType) => {
    if (modalType === 'VAT') {
      setIsVatModalOpen(true);
    } else {
      setIsPriceModalOpen(true);
    }
  };

  const closeModal = (modalType: EntryModifiedModalType) => {
    if (modalType === 'VAT') {
      setIsVatModalOpen(false);
    } else {
      setIsPriceModalOpen(false);
    }
  };

  return {
    VatChangedModal,
    PriceChangedModal,
    openModal,
    closeModal,
  };
};
